import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  Target, AlertTriangle, TrendingUp, DollarSign, Clock, Zap,
  CheckCircle, XCircle, AlertCircle, Plus, RefreshCw, ArrowRight
} from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAGE_COLORS = {
  ideation: 'bg-blue-500/20 text-blue-500',
  planning: 'bg-purple-500/20 text-purple-500',
  approved: 'bg-green-500/20 text-green-500',
  in_progress: 'bg-yellow-500/20 text-yellow-500',
  at_risk: 'bg-orange-500/20 text-orange-500',
  stalled: 'bg-red-500/20 text-red-500',
  completed: 'bg-green-500/20 text-green-500',
  cancelled: 'bg-gray-500/20 text-gray-500'
};

const RISK_COLORS = {
  none: 'bg-gray-500/20 text-gray-500',
  low: 'bg-green-500/20 text-green-500',
  medium: 'bg-yellow-500/20 text-yellow-500',
  high: 'bg-orange-500/20 text-orange-500',
  critical: 'bg-red-500/20 text-red-500'
};

const InitiativeCommandCenter = () => {
  const [loading, setLoading] = useState(true);
  const [commandCenter, setCommandCenter] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchCommandCenter();
  }, []);

  const fetchCommandCenter = async () => {
    try {
      setRefreshing(true);
      
      const response = await axios.get(
        `${API}/execution/command-center`,
        { headers: getAuthHeaders() }
      );
      
      setCommandCenter(response.data);
    } catch (error) {
      console.error('Failed to fetch command center:', error);
      toast.error('Failed to load command center');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            <p className="text-muted-foreground mt-4">Loading Command Center...</p>
          </div>
        </div>
      </Layout>
    );
  }

  const initiatives = commandCenter?.initiatives || [];
  const capitalExposure = commandCenter?.capital_exposure || {};
  const highestLeverage = commandCenter?.highest_leverage_action;
  const stalledCount = commandCenter?.stalled_count || 0;
  const missedDeadlinesCount = commandCenter?.missed_deadlines_count || 0;

  return (
    <Layout>
      <div className="space-y-8" data-testid="initiative-command-center">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-5xl font-bold text-white mb-3">INITIATIVE COMMAND CENTER</h1>
            <p className="text-muted-foreground text-base sm:text-lg">
              Complete execution visibility • Capital tracking • Chief of Staff monitoring
            </p>
          </div>
          <div className="flex gap-3 shrink-0">
            <button
              onClick={fetchCommandCenter}
              disabled={refreshing}
              className="bg-secondary/50 hover:bg-secondary text-white px-5 py-3 flex items-center gap-2 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm font-semibold">Refresh</span>
            </button>
          </div>
        </div>

        {/* Highest Leverage Action */}
        {highestLeverage && (
          <div className="glass-card p-8 border border-primary/30 bg-primary/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-primary/10 rounded-full blur-3xl"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="w-6 h-6 text-primary" />
                <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
                  HIGHEST LEVERAGE ACTION RIGHT NOW
                </h2>
              </div>
              
              <div className="bg-background/50 p-6 border border-primary/20 mb-4">
                <h3 className="text-2xl font-bold text-white mb-3">{highestLeverage.name}</h3>
                <p className="text-primary mb-4 font-semibold">→ {highestLeverage.next_action}</p>
                <p className="text-sm text-muted-foreground mb-4">{highestLeverage.why_highest_leverage}</p>
                <div className="flex items-center gap-4">
                  <span className={`text-xs uppercase px-3 py-1 ${STAGE_COLORS[highestLeverage.stage]}`}>
                    {highestLeverage.stage.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-white font-mono">
                    Leverage Score: {highestLeverage.leverage_score.toFixed(0)}
                  </span>
                </div>
              </div>
              
              <p className="text-xs text-muted-foreground uppercase tracking-wider">
                ✓ This is your single most impactful action across your entire portfolio
              </p>
            </div>
          </div>
        )}

        {/* Capital Exposure Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="glass-card p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total Committed</span>
              <DollarSign className="w-5 h-5 text-primary" />
            </div>
            <p className="text-3xl font-bold font-mono text-white">
              ${(capitalExposure.total_committed || 0).toLocaleString()}
            </p>
          </div>

          <div className="glass-card p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Total Deployed</span>
              <TrendingUp className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold font-mono text-white">
              ${(capitalExposure.total_deployed || 0).toLocaleString()}
            </p>
          </div>

          <div className="glass-card p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">At-Risk Capital</span>
              <AlertTriangle className="w-5 h-5 text-orange-500" />
            </div>
            <p className="text-3xl font-bold font-mono text-orange-500">
              ${(capitalExposure.at_risk_capital || 0).toLocaleString()}
            </p>
          </div>

          <div className="glass-card p-6 border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Active Initiatives</span>
              <Target className="w-5 h-5 text-white" />
            </div>
            <p className="text-3xl font-bold font-mono text-white">
              {initiatives.length}
            </p>
          </div>
        </div>

        {/* Alert Bar */}
        {(stalledCount > 0 || missedDeadlinesCount > 0) && (
          <div className="glass-card p-4 border border-yellow-500/30 bg-yellow-500/5 flex items-center gap-4">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-white font-semibold">
                {stalledCount > 0 && `${stalledCount} stalled initiative${stalledCount > 1 ? 's' : ''}`}
                {stalledCount > 0 && missedDeadlinesCount > 0 && ' • '}
                {missedDeadlinesCount > 0 && `${missedDeadlinesCount} missed deadline${missedDeadlinesCount > 1 ? 's' : ''}`}
              </p>
              <p className="text-xs text-muted-foreground">Proactive detection - Lee monitoring continuously</p>
            </div>
          </div>
        )}

        {/* All Initiatives */}
        <div className="glass-card p-8 border border-white/10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white uppercase tracking-tight">
              ALL ACTIVE INITIATIVES
            </h2>
            <button className="bg-primary/20 hover:bg-primary/30 text-primary px-4 py-2 flex items-center gap-2 transition-colors border border-primary/30">
              <Plus className="w-4 h-4" />
              <span className="text-sm font-semibold">New Initiative</span>
            </button>
          </div>

          {initiatives.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-white mb-2">No active initiatives</p>
              <p className="text-sm text-muted-foreground">Create your first initiative to start tracking execution</p>
            </div>
          ) : (
            <div className="space-y-4">
              {initiatives.map((initiative, idx) => (
                <div
                  key={idx}
                  className="bg-secondary/20 p-6 border border-border hover:border-primary/30 transition-colors"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{initiative.name}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{initiative.description}</p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-xs uppercase px-3 py-1 ${STAGE_COLORS[initiative.stage]}`}>
                        {initiative.stage.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-xs uppercase px-3 py-1 ${RISK_COLORS[initiative.risk_level]}`}>
                        {initiative.risk_level} Risk
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Capital Committed</p>
                      <p className="text-white font-mono">${(initiative.capital_committed || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Capital Deployed</p>
                      <p className="text-white font-mono">${(initiative.capital_deployed || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Category</p>
                      <p className="text-white capitalize">{initiative.category}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">Decision Owner</p>
                      <p className="text-white">{initiative.decision_owner}</p>
                    </div>
                  </div>

                  {initiative.expected_outcome && (
                    <div className="bg-background/50 p-3 border border-white/5 mb-4">
                      <p className="text-xs text-muted-foreground mb-1">Expected Outcome</p>
                      <p className="text-sm text-white">{initiative.expected_outcome}</p>
                    </div>
                  )}

                  {initiative.active_blockers && initiative.active_blockers.length > 0 && (
                    <div className="bg-red-500/10 p-3 border border-red-500/20 mb-4 flex items-center gap-3">
                      <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-500 font-semibold">
                        {initiative.active_blockers.length} active blocker{initiative.active_blockers.length > 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      Last update: {new Date(initiative.last_progress_update).toLocaleDateString()}
                    </div>
                    <button className="text-sm text-primary hover:underline flex items-center gap-2">
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Capital Exposure by Category */}
        {capitalExposure.by_category && Object.keys(capitalExposure.by_category).length > 0 && (
          <div className="glass-card p-8 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-6 uppercase tracking-tight">
              CAPITAL ALLOCATION BY CATEGORY
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(capitalExposure.by_category).map(([category, amount]) => (
                <div key={category} className="bg-background/50 p-6 border border-white/5">
                  <p className="text-sm text-muted-foreground mb-2 uppercase tracking-wider">{category}</p>
                  <p className="text-2xl font-bold font-mono text-white">
                    ${amount.toLocaleString()}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default InitiativeCommandCenter;
